/**
 * wholesale-addons.js — Factory1 Direct
 * ------------------------------------------------------------
 * Add-ons de servicios POR PRODUCTO para clientes wholesale con
 * 100+ piezas totales en el carrito.
 *
 * MODELO DE INTERACCIÓN: el cliente marca/desmarca todos los
 * checkboxes que quiera (Screen Print, sub-opciones, tintas,
 * labels, packing, etc.) SIN que nada se mande al carrito todavía
 * — son cambios locales en el navegador. Solo cuando le da click
 * al botón "Confirm services" se procesan todos los cambios de
 * golpe (agregar/quitar líneas, actualizar properties) y se hace
 * UN SOLO refresh de página al final.
 *
 * Esto evita: 1) que cada click dispare una llamada de red +
 * reload (lento en internet lento), y 2) los problemas de que
 * otras apps del tema (ImprintNext, WSH Wholesale Pricing) solo
 * recalculan sus cosas (imágenes de diseño, precios con descuento)
 * en una carga completa de página — con un solo reload al final,
 * todas esas apps corren normal, sin necesitar parches.
 *
 * SETUP REQUERIDO EN SHOPIFY ADMIN:
 * 1. Producto "Wholesale Add-on Services" publicado en canal Online
 *    Store, una variante por servicio, Track quantity: OFF en todas.
 * 2. Variante "Screen Print - Tee" renombrada a "Screen Print"
 *    (se reusa como componente base para Tee y Fleece).
 * 3. Excluir este producto de la app Wholesale Pricing Discount B2B.
 *
 * WHOLESALE 2.0 (completo):
 * 4. Screenprint — "Front" reusa el variantId que ya existía como
 *    "Screen Print". Back, Sleeve 1, Sleeve 2 y Extra Print son
 *    variantes nuevas ya configuradas ($0.75 c/u).
 * 5. MOQ — 4 variantes nuevas, una por tier, ya configuradas con el
 *    precio real puesto en Shopify (2.50 / 1.50 / 0.75 / 0.25). Cargo
 *    automático que se agrega según el total de piezas del pedido.
 * 6. San Diego Distribution Center YA NO es un servicio del sistema
 *    de add-ons — ese costo ahora vive incluido en el precio base del
 *    producto directamente en Shopify. El servicio y su variante se
 *    eliminaron.
 */

(function () {
  'use strict';

  const MIN_PIECES_FOR_ADDONS = 100;

  // ------------------------------------------------------------
  // Detección instantánea de cambios de carrito NO iniciados por
  // nosotros (el +/- nativo del carrito, el cart drawer del tema,
  // etc.). Antes de esto, la única señal era un MutationObserver
  // viendo un nodo del DOM que el tema a veces reemplaza entero (el
  // observer se queda "ciego") más un polling que, pasados los
  // primeros 10s de la carga de página, solo revisa cada 5s — el
  // cliente podía tardar varios segundos, o necesitar un refresh
  // manual, en ver los servicios ajustados.
  //
  // En vez de eso, interceptamos fetch: cualquier POST a
  // /cart/change.js, /cart/add.js o /cart/update.js que NO hicimos
  // nosotros mismos (cartMutationInProgress ya estaría en true si
  // fuéramos nosotros) dispara el resync apenas esa petición nativa
  // termina — sin esperar al DOM ni al timer.
  const nativeFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const method = ((init && init.method) || 'GET').toUpperCase();
    const isCartMutation = method === 'POST' && /\/cart\/(change|add|update)\.js(\?|$)/.test(url);

    const promise = nativeFetch(input, init);

    if (isCartMutation && !cartMutationInProgress) {
      promise
        .then(() => {
          scheduleRecompute();
        })
        .catch(() => {});
    }

    return promise;
  };

  // Aviso visual mientras se ajustan cantidades/servicios en segundo
  // plano, para que el cliente no piense que la página se quedó
  // pegada mientras esperamos las llamadas a Shopify.
  let syncToastEl = null;
  function showSyncingBanner() {
    if (syncToastEl) return;

    if (!document.getElementById('wholesale-addons-spin-keyframes')) {
      const style = document.createElement('style');
      style.id = 'wholesale-addons-spin-keyframes';
      style.textContent = '@keyframes wholesale-addons-spin { to { transform: rotate(360deg); } }';
      document.head.appendChild(style);
    }

    syncToastEl = document.createElement('div');
    syncToastEl.id = 'wholesale-addons-sync-toast';
    syncToastEl.setAttribute('role', 'status');
    syncToastEl.setAttribute('aria-live', 'polite');
    syncToastEl.style.cssText =
      'position:fixed;bottom:20px;right:20px;z-index:99999;background:#111;color:#fff;' +
      'padding:12px 16px;border-radius:8px;font-size:14px;display:flex;align-items:center;' +
      'gap:10px;box-shadow:0 4px 12px rgba(0,0,0,0.25);font-family:inherit;';

    const spinner = document.createElement('span');
    spinner.style.cssText =
      'width:14px;height:14px;border-radius:50%;border:2px solid rgba(255,255,255,0.35);' +
      'border-top-color:#fff;display:inline-block;animation:wholesale-addons-spin 0.7s linear infinite;';

    const text = document.createElement('span');
    text.textContent = 'Updating services for your quantity change…';

    syncToastEl.appendChild(spinner);
    syncToastEl.appendChild(text);
    document.body.appendChild(syncToastEl);
  }

  function hideSyncingBanner() {
    if (syncToastEl) {
      syncToastEl.remove();
      syncToastEl = null;
    }
  }

  // ------------------------------------------------------------
  // Screen Print — placements (una o varias, el cliente elige) +
  // modificadores (solo aplican si hay al menos un placement elegido)
  // ------------------------------------------------------------
  const SCREEN_PRINT_FLEECE_KEY = 'printing_fleece';
  const SCREEN_PRINT_FLEECE_LABEL = 'Printing on Fleece / Short/Pant (upcharge)';
  const SCREEN_PRINT_FLEECE_PRICE = 0.25;
  const SCREEN_PRINT_FLEECE_VARIANT = '44140512870534';

  // "Front" REUSA la variante que ya existía como "Screen Print" — no es
  // un servicio nuevo, es la misma ubicación de estampado de siempre,
  // solo renombrada. Back / Sleeve 1 / Sleeve 2 / Extra Print sí son
  // nuevas y necesitan su propia variante en Shopify (REPLACE_ME).
  //
  // dependsOn: la opción solo aparece si ya está marcada alguna de las
  // opciones listadas ahí. Sleeve 2 pide Sleeve 1 primero; Extra Print
  // pide que ya haya al menos un lado (Front o Back) marcado.
  const SCREEN_PRINT_PLACEMENTS = [
    { key: 'screenprint_front', label: 'Front', price: 0.75, variantId: '44140512772230', appliesTo: ['all'] },
    { key: 'screenprint_back', label: 'Back', price: 0.75, variantId: '44152587059334', appliesTo: ['all'] },
    { key: 'screenprint_sleeve1', label: 'Sleeve 1', price: 0.75, variantId: '44152594464902', appliesTo: ['all'] },
    { key: 'screenprint_sleeve2', label: 'Sleeve 2', price: 0.75, variantId: '44152594956422', appliesTo: ['all'], dependsOn: ['screenprint_sleeve1'] },
    { key: 'screenprint_extraprint', label: 'Extra Print', price: 0.75, variantId: '44152597151878', appliesTo: ['all'], dependsOn: ['screenprint_front', 'screenprint_back'] },
    { key: 'screenprint_wingpallet_1color', label: 'Wing Pallet 1 Color', price: 1.00, variantId: '44179338592390', appliesTo: ['all'] },
    { key: 'screenprint_wingpallet_2color', label: 'Wing Pallet 2 Color', price: 1.25, variantId: '44179338625158', appliesTo: ['all'] },
    { key: 'screenprint_wingpallet_3color', label: 'Wing Pallet 3 Color', price: 1.35, variantId: '44179338657926', appliesTo: ['all'] },
  ];

  const SCREEN_PRINT_PLACEMENT_KEYS = new Set(SCREEN_PRINT_PLACEMENTS.map((p) => p.key));

  // Wing Pallet: son 3 variantes de la MISMA ubicación (distinto número
  // de colores), no se pueden combinar entre sí — el cliente elige una
  // sola, como pasa con Specialty Ink. Siguen contando como "placement"
  // para efectos de desbloquear modificadores/tinta y para dependsOn de
  // Extra Print, pero se renderizan como radio, no como checkbox.
  const WING_PALLET_KEYS = new Set([
    'screenprint_wingpallet_1color',
    'screenprint_wingpallet_2color',
    'screenprint_wingpallet_3color',
  ]);

  const SCREEN_PRINT_MODIFIERS = [
    { key: 'jumbo_print', label: "Jumbo Print (more than 18.5'')", price: 0.20, variantId: '44140512804998', appliesTo: ['tee'] },
    { key: 'discharge_waterbase', label: 'Discharge or Waterbase', price: 0.10, variantId: '44140512837766', appliesTo: ['all'] },
  ];

  const SPECIALTY_INKS = [
    { key: 'puff_6x6', label: 'Puff 6x6', price: 0.27, variantId: '44140513394822' },
    { key: 'puff_6x12', label: 'Puff 6x12', price: 0.37, variantId: '44140513427590' },
    { key: 'puff_12x12', label: 'Puff 12x12', price: 0.67, variantId: '44140513460358' },
    { key: 'hidensity_6x6', label: 'Hi Density 6x6', price: 0.32, variantId: '44140513493126' },
    { key: 'hidensity_6x12', label: 'Hi Density 6x12', price: 0.42, variantId: '44140513525894' },
    { key: 'hidensity_12x12', label: 'Hi Density 12x12', price: 0.72, variantId: '44140513558662' },
    { key: 'gel_6x6', label: 'Gel 6x6', price: 0.22, variantId: '44140513591430' },
    { key: 'gel_6x12', label: 'Gel 6x12', price: 0.32, variantId: '44140513624198' },
    { key: 'gel_12x12', label: 'Gel 12x12', price: 0.62, variantId: '44140513656966' },
    { key: 'glow_dark_6x6', label: 'Glow in the Dark 6x6', price: 0.37, variantId: '44140513689734' },
    { key: 'glow_dark_6x12', label: 'Glow in the Dark 6x12', price: 0.47, variantId: '44140513722502' },
    { key: 'glow_dark_12x12', label: 'Glow in the Dark 12x12', price: 0.77, variantId: '44140513755270' },
    { key: 'foil_6x6', label: 'Foil (Gold/Silver) 6x6', price: 0.47, variantId: '44140513788038' },
    { key: 'foil_6x12', label: 'Foil (Gold/Silver) 6x12', price: 0.52, variantId: '44140513820806' },
    { key: 'foil_12x12', label: 'Foil (Gold/Silver) 12x12', price: 0.67, variantId: '44140513853574' },
    { key: 'printed_glitter_6x6', label: 'Printed Glitter 6x6', price: 0.27, variantId: '44140513886342' },
    { key: 'printed_glitter_6x12', label: 'Printed Glitter 6x12', price: 0.37, variantId: '44140513919110' },
    { key: 'printed_glitter_12x12', label: 'Printed Glitter 12x12', price: 0.62, variantId: '44140513951878' },
    { key: 'sprinkled_glitter_6x6', label: 'Sprinkled Glitter 6x6', price: 0.32, variantId: '44140513984646' },
    { key: 'sprinkled_glitter_6x12', label: 'Sprinkled Glitter 6x12', price: 0.47, variantId: '44140514017414' },
    { key: 'sprinkled_glitter_12x12', label: 'Sprinkled Glitter 12x12', price: 0.77, variantId: '44140514050182' },
    { key: 'metallic_6x6', label: 'Metallic 6x6', price: 0.27, variantId: '44140514082950' },
    { key: 'metallic_6x12', label: 'Metallic 6x12', price: 0.37, variantId: '44140514115718' },
    { key: 'metallic_12x12', label: 'Metallic 12x12', price: 0.47, variantId: '44140514148486' },
  ];

  const OTHER_SERVICES = [
    { key: 'pad_print_1color', group: 'Labels', label: 'Pad Print 1 Color - Tee', price: 0.30, variantId: '44140512903302', appliesTo: ['tee'] },
    { key: 'pad_print_crewneck_fleece', group: 'Labels', label: 'Pad Print Crew Neck Fleece / Short/Pant', price: 0.40, variantId: '44140512936070', appliesTo: ['fleece'] },
    { key: 'pad_print_hoodie', group: 'Labels', label: 'Pad Print Hoodie', price: 0.30, variantId: '44140512968838', appliesTo: ['fleece'] },
    { key: 'sew_neck_label_1side', group: 'Labels', label: 'Sew Neck Label (One Side)', price: 0.30, variantId: '44140513001606', appliesTo: ['all'] },
    { key: 'sew_neck_label_4sides', group: 'Labels', label: 'Sew Neck Label (Four Sides)', price: 0.45, variantId: '44140513034374', appliesTo: ['all'] },
    { key: 'sew_clip_hem_label', group: 'Labels', label: 'Sew Clip/Hem Label', price: 0.25, variantId: '44140513067142', appliesTo: ['all'] },
    { key: 'remove_woven_label', group: 'Labels', label: 'Remove Woven Label', price: 0.15, variantId: '44140513099910', appliesTo: ['all'] },

    { key: 'packing_tee', group: 'Packing / Distribution', label: 'Packing Tee', price: 0.35, variantId: '44140513132678', appliesTo: ['tee'] },
    { key: 'packing_fleece_hoodie', group: 'Packing / Distribution', label: 'Packing Fleece / Hoodie / Short/Pant', price: 0.40, variantId: '44140513165446', appliesTo: ['fleece'] },

    { key: 'crop_hem_tee', group: 'Packing Up Charges', label: 'Crop & Hem - Tee', price: 0.95, variantId: '44140513296518', appliesTo: ['tee'] },
    { key: 'grinding_tee', group: 'Packing Up Charges', label: 'Grinding (Neck, Hem & Sleeves) - Tee', price: 0.80, variantId: '44140513329286', appliesTo: ['tee'] },
    { key: 'holes_tee', group: 'Packing Up Charges', label: 'Holes (Front, Sleeves & Hem) - Tee', price: 1.00, variantId: '44140513362054', appliesTo: ['tee'] },
    { key: 'Heat press', group: 'Packing Up Charges', label: 'Heat press (for patch application)', price: 0.45, variantId: '44216967823494', appliesTo: ['all'] },
  ];

  // MOQ — cargo automático (no lo marca el cliente) que se suma al
  // precio unitario del producto según el total de piezas del pedido.
  // Cada tier necesita su PROPIA variante en Shopify con el precio
  // real puesto ahí (2.50 / 1.50 / 0.75 / 0.25), porque lo que cobra
  // Shopify es el precio de la variante, no el número "price" de acá
  // (ese "price" solo es para mostrarlo en el banner informativo).
  const SETUP_MOQ_TIERS = [
    { key: 'moq_101_200', min: 100, max: 200, price: 2.95, variantId: '44152610783366', label: 'MOQ Setup Charge (100-200 units)' },
    { key: 'moq_201_300', min: 201, max: 300, price: 1.95, variantId: '44152610816134', label: 'MOQ Setup Charge (201-300 units)' },
    { key: 'moq_301_599', min: 301, max: 599, price: 1.10, variantId: '44152610848902', label: 'MOQ Setup Charge (301-599 units)' },
    { key: 'moq_600_1199', min: 600, max: 1199, price: 0.50, variantId: '44152610881670', label: 'MOQ Setup Charge (600-1199 units)' },
  ];

  const MOQ_KEYS = new Set(SETUP_MOQ_TIERS.map((t) => t.key));

  function getSetupMoqTier(pieceCount) {
    return SETUP_MOQ_TIERS.find((t) => pieceCount >= t.min && pieceCount <= t.max) || null;
  }

  const ALL_SIMPLE_SERVICES = [
    ...SCREEN_PRINT_PLACEMENTS,
    { key: SCREEN_PRINT_FLEECE_KEY, label: SCREEN_PRINT_FLEECE_LABEL, price: SCREEN_PRINT_FLEECE_PRICE, variantId: SCREEN_PRINT_FLEECE_VARIANT },
    ...SCREEN_PRINT_MODIFIERS,
    ...SPECIALTY_INKS,
    ...OTHER_SERVICES,
    ...SETUP_MOQ_TIERS,
  ];

  function findServiceByKey(key) {
    return ALL_SIMPLE_SERVICES.find((s) => s.key === key);
  }

  // ------------------------------------------------------------
  // Detección de tipo de prenda POR PRODUCTO
  // ------------------------------------------------------------
  const PRODUCT_TYPE_KEYWORDS = {
    tee: ['tee', 't-shirt', 'tshirt'],
    // shorts/pants comparten los mismos servicios que fleece (Pad Print
    // Crew Neck Fleece, Packing Fleece / Hoodie, el upcharge de Printing
    // on Fleece, etc.) — por eso viven en el mismo bucket de detección
    // en vez de crear una categoría 'bottoms' aparte.
    fleece: ['fleece', 'hoodie', 'sweatshirt', 'crewneck', 'crew neck', 'crew', 'pullover', 'short', 'shorts', 'pant', 'pants'],
  };

  function detectItemTypes(title) {
    const lowerTitle = (title || '').toLowerCase();
    const types = new Set();
    Object.entries(PRODUCT_TYPE_KEYWORDS).forEach(([type, keywords]) => {
      if (keywords.some((kw) => lowerTitle.includes(kw))) {
        types.add(type);
      }
    });
    return types;
  }

  function serviceApplies(service, itemTypes) {
    if (!service.appliesTo || service.appliesTo.includes('all')) return true;
    return service.appliesTo.some((t) => itemTypes.has(t));
  }

  // ------------------------------------------------------------
  // Helpers de carrito (Shopify Ajax API nativa)
  // ------------------------------------------------------------
  async function getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  function generateRef() {
    return Math.random().toString(36).slice(2, 10);
  }

  function getParentLines(cart) {
    return cart.items.filter((item) => !(item.properties && item.properties._wholesale_addon));
  }

  function getAddonLines(cart) {
    return cart.items.filter((item) => item.properties && item.properties._wholesale_addon);
  }

  function getParentByRef(cart, ref) {
    return cart.items.find((item) => item.properties && item.properties._wholesale_ref === ref);
  }

  function totalPieceCount(cart) {
    return getParentLines(cart).reduce((sum, item) => sum + item.quantity, 0);
  }

  // El MOQ es por DISEÑO, no por el carrito completo — un cliente que
  // pide 210 piezas de un diseño y 50 de otro distinto no debe
  // "compartir" tier entre ellos; cada diseño se evalúa con SU propio
  // total (sumando todas sus tallas).
  function designPieceCount(cart, designKey) {
    return getParentLines(cart)
      .filter((p) => getDesignKey(p) === designKey)
      .reduce((sum, p) => sum + p.quantity, 0);
  }

  async function updateLineProperties(lineIndex1Based, quantity, mergedProperties) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: lineIndex1Based, quantity, properties: mergedProperties }),
    });
    return res.json();
  }

  function buildProductLabel(parentLine) {
    const base = `${parentLine.product_title || parentLine.title} — ${parentLine.variant_title || ''}`;
    const refId = parentLine.properties && parentLine.properties._refid;
    if (refId) return `${base} (Design #${refId})`;
    const shortRef = (parentLine.key || '').split(':')[1];
    if (shortRef) return `${base} (Ref: ${shortRef.slice(0, 6)})`;
    return base;
  }

  // Imagen del diseño para diferenciar visualmente bloques del mismo
  // producto con diseños distintos (igual que ya se ve en el carrito).
  // Confirmado: item.image de la Ajax Cart API YA es el mockup real
  // del diseño (lo mete ImprintNext), no la imagen genérica del producto.
  function getDesignImageUrl(parentLine) {
    return parentLine.image || null;
  }

  async function addServiceLine(variantId, key, label, quantity, ref, productLabel) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: quantity,
            properties: {
              'For Product': productLabel,
              _wholesale_addon: key,
              _addon_label: label,
              _for_line: ref,
            },
          },
        ],
      }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'The service could not be added to the cart');
    }
    return res.json();
  }

  function findAddonLineIndexByRef(cart, key, ref) {
    return cart.items.findIndex(
      (item) => item.properties && item.properties._wholesale_addon === key && item.properties._for_line === ref
    );
  }

  async function removeCartLine(lineIndex1Based) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: lineIndex1Based, quantity: 0 }),
    }).then((r) => r.json());
  }

  async function updateLineQuantity(lineIndex1Based, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: lineIndex1Based, quantity }),
    }).then((r) => r.json());
  }

  function isCustomerWholesale(root) {
    return root.dataset.customerWholesale === 'true';
  }

  // ------------------------------------------------------------
  // Estado local (pendiente de confirmar) — nada de esto toca el
  // carrito hasta que se le dé click a "Confirm services".
  // ------------------------------------------------------------
  // pendingState[designKey] = { subKeys: Set<string> }
  // "Screen print activo" ya no es un booleano aparte: se calcula viendo
  // si subKeys tiene AL MENOS un placement (Front/Back/Sleeve1/Sleeve2/
  // Extra Print). El estado se comparte por DISEÑO (no por línea de
  // producto), así que el mismo diseño en distintas tallas siempre trae
  // los mismos servicios.
  let pendingState = {};
  let hasPendingChanges = false;

  // Lock global: Confirm services, Clear all services, y el auto-sync de
  // fondo (autoSyncQuantities, vía scheduleRecompute) mutan el carrito
  // por ÍNDICE DE POSICIÓN (/cart/change.js con "line": N). Si dos de
  // estos procesos corren al mismo tiempo, uno calcula índices sobre un
  // carrito que el otro ya movió por debajo — eso es lo que causó que
  // "Clear all services" borrara los servicios de ambas tallas de golpe
  // (el poll de fondo se disparó a mitad del loop del botón y pisó sus
  // índices). Todo lo que llame a applyDesiredServicesForLine en un loop
  // debe tomar este lock primero y soltarlo siempre en un finally.
  let cartMutationInProgress = false;

  // Mismo diseño = mismo _refid de ImprintNext si existe; si no hay
  // _refid, se agrupa por product_id (mismo producto, distinta talla).
  function getDesignKey(parentLine) {
    const props = parentLine.properties || {};
    if (props._refid) return `refid:${props._refid}`;
    return `product:${parentLine.product_id}`;
  }

  function buildInitialStateForDesign(designKey, cart) {
    const siblingParents = getParentLines(cart).filter((p) => getDesignKey(p) === designKey);
    const refs = new Set(
      siblingParents.map((p) => p.properties && p.properties._wholesale_ref).filter(Boolean)
    );
    const addonLinesForDesign = getAddonLines(cart).filter((a) => refs.has(a.properties._for_line));
    const subKeys = new Set(
      addonLinesForDesign.map((a) => a.properties._wholesale_addon)
    );
    return { subKeys };
  }

  // ------------------------------------------------------------
  // Render — un bloque por producto
  // ------------------------------------------------------------
  function buildCheckboxRow(service, checked, isConfigured, onToggle) {
    const label = document.createElement('label');
    label.className = 'wholesale-addons__item';
    if (!isConfigured) label.classList.add('wholesale-addons__item--disabled');

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'wholesale-addon-checkbox';
    input.checked = checked;
    input.disabled = !isConfigured;

    const text = document.createElement('span');
    text.textContent = `${service.label} ($${service.price.toFixed(2)}/pc)`;

    label.appendChild(input);
    label.appendChild(text);

    input.addEventListener('change', () => onToggle(input.checked));

    return label;
  }

  // Tintas especiales: solo se puede escoger una (radio), no checkbox.
  function buildRadioRow(labelText, name, checked, isConfigured, onSelect) {
    const label = document.createElement('label');
    label.className = 'wholesale-addons__item';
    if (!isConfigured) label.classList.add('wholesale-addons__item--disabled');

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = name;
    input.className = 'wholesale-addon-checkbox';
    input.checked = checked;
    input.disabled = !isConfigured;

    const text = document.createElement('span');
    text.textContent = labelText;

    label.appendChild(input);
    label.appendChild(text);

    input.addEventListener('change', () => {
      if (input.checked) onSelect();
    });

    return label;
  }

  // Re-renderiza TODOS los bloques de producto (necesario porque el
  // estado ahora es por diseño: tocar una talla debe reflejarse en las
  // demás tallas del mismo diseño, no solo en el bloque que se tocó).
  function rerenderAllBlocks(cart) {
    const groupsContainer = document.querySelector('.wholesale-addons__groups');
    const panel = document.querySelector('.wholesale-addons__panel');
    const scrollTop = panel ? panel.scrollTop : 0;
    renderProductBlocks(groupsContainer, cart);
    if (panel) panel.scrollTop = scrollTop;
  }

  function buildProductBlock(parentLines, cart) {
    // parentLines = todas las líneas del carrito que son el MISMO diseño
    // (distintas tallas incluidas). Un solo bloque visual por diseño.
    const firstParent = parentLines[0];
    const itemTypes = detectItemTypes(firstParent.product_title || firstParent.title);
    const isTee = itemTypes.has('tee');
    const isFleece = itemTypes.has('fleece');

    const designKey = getDesignKey(firstParent);
    if (!pendingState[designKey]) {
      pendingState[designKey] = buildInitialStateForDesign(designKey, cart);
    }
    const state = pendingState[designKey];

    const details = document.createElement('details');
    details.className = 'wholesale-addons__product-block';
    details.dataset.designKey = designKey;
    details.open = state.subKeys.size > 0;

    const summary = document.createElement('summary');
    summary.className = 'wholesale-addons__product-title';

    // Miniatura del diseño (misma referencia visual que ya ve el
    // cliente en el carrito) — así se distingue un diseño de otro
    // aunque sea el mismo producto, sin exponer números internos.
    const imageUrl = getDesignImageUrl(firstParent);
    if (imageUrl) {
      const thumb = document.createElement('img');
      thumb.src = imageUrl;
      thumb.alt = '';
      thumb.className = 'wholesale-addons__product-thumb';
      summary.appendChild(thumb);
    }

    const titleText = document.createElement('span');
    // Solo el nombre del producto — sin piezas ni número de diseño, eso
    // es info de backend, no le interesa al cliente en este modal.
    titleText.textContent = firstParent.product_title || firstParent.title;
    summary.appendChild(titleText);

    details.appendChild(summary);

    // ---- Screen Print (placements: el cliente puede elegir varios) ----
    if (isTee || isFleece) {
      const spGroup = document.createElement('div');
      spGroup.className = 'wholesale-addons__group';

      const spTitle = document.createElement('h4');
      spTitle.className = 'wholesale-addons__group-title';
      spTitle.textContent = 'Screenprint';
      spGroup.appendChild(spTitle);

      // Si una opción con dependencia (Sleeve 2, Extra Print) quedó
      // marcada pero su dependencia ya no está, se limpia — si no,
      // se seguiría cobrando algo que ya no se ve en pantalla.
      function placementUnlocked(service) {
        if (!service.dependsOn) return true;
        return service.dependsOn.some((k) => state.subKeys.has(k));
      }
      SCREEN_PRINT_PLACEMENTS.forEach((s) => {
        if (s.dependsOn && !placementUnlocked(s) && state.subKeys.has(s.key)) {
          state.subKeys.delete(s.key);
        }
      });

      SCREEN_PRINT_PLACEMENTS.filter(
        (s) => serviceApplies(s, itemTypes) && placementUnlocked(s) && !WING_PALLET_KEYS.has(s.key)
      ).forEach((service) => {
        const isConf = service.variantId && service.variantId !== 'REPLACE_ME';
        const displayPrice = isFleece ? service.price + SCREEN_PRINT_FLEECE_PRICE : service.price;
        const displayService = { ...service, price: displayPrice };
        spGroup.appendChild(
          buildCheckboxRow(displayService, state.subKeys.has(service.key), isConf, (checked) => {
            if (checked) state.subKeys.add(service.key);
            else state.subKeys.delete(service.key);
            markPendingChanged();
            rerenderAllBlocks(cart);
          })
        );
      });

      // Wing Pallet 1/2/3 Color: solo una de las tres a la vez.
      const wingPalletTitle = document.createElement('p');
      wingPalletTitle.className = 'wholesale-addons__subgroup-title';
      wingPalletTitle.textContent = 'Wing Pallet (optional, choose one):';
      spGroup.appendChild(wingPalletTitle);

      const selectedWingPallet = [...state.subKeys].find((k) => WING_PALLET_KEYS.has(k)) || null;
      const wingPalletRadioName = `wholesale-wingpallet-${designKey}`;

      spGroup.appendChild(
        buildRadioRow('None', wingPalletRadioName, !selectedWingPallet, true, () => {
          WING_PALLET_KEYS.forEach((k) => state.subKeys.delete(k));
          markPendingChanged();
          rerenderAllBlocks(cart);
        })
      );

      SCREEN_PRINT_PLACEMENTS.filter((s) => WING_PALLET_KEYS.has(s.key) && serviceApplies(s, itemTypes)).forEach((service) => {
        const isConf = service.variantId && service.variantId !== 'REPLACE_ME';
        const displayPrice = isFleece ? service.price + SCREEN_PRINT_FLEECE_PRICE : service.price;
        spGroup.appendChild(
          buildRadioRow(
            `${service.label} ($${displayPrice.toFixed(2)}/pc)`,
            wingPalletRadioName,
            selectedWingPallet === service.key,
            isConf,
            () => {
              WING_PALLET_KEYS.forEach((k) => state.subKeys.delete(k));
              state.subKeys.add(service.key);
              markPendingChanged();
              rerenderAllBlocks(cart);
            }
          )
        );
      });

      const anyPlacementSelected = [...state.subKeys].some((k) => SCREEN_PRINT_PLACEMENT_KEYS.has(k));

      if (anyPlacementSelected) {
        const subWrap = document.createElement('div');
        subWrap.className = 'wholesale-addons__subgroup';

        SCREEN_PRINT_MODIFIERS.filter((s) => serviceApplies(s, itemTypes)).forEach((service) => {
          const isConf = service.variantId && service.variantId !== 'REPLACE_ME';
          subWrap.appendChild(
            buildCheckboxRow(service, state.subKeys.has(service.key), isConf, (checked) => {
              if (checked) state.subKeys.add(service.key);
              else state.subKeys.delete(service.key);
              markPendingChanged();
              rerenderAllBlocks(cart);
            })
          );
        });

        const inkTitle = document.createElement('p');
        inkTitle.className = 'wholesale-addons__subgroup-title';
        inkTitle.textContent = 'Specialty Ink (optional, choose one):';
        subWrap.appendChild(inkTitle);

        const inkKeys = new Set(SPECIALTY_INKS.map((s) => s.key));
        const selectedInk = [...state.subKeys].find((k) => inkKeys.has(k)) || null;
        const inkRadioName = `wholesale-ink-${designKey}`;

        subWrap.appendChild(
          buildRadioRow('None', inkRadioName, !selectedInk, true, () => {
            inkKeys.forEach((k) => state.subKeys.delete(k));
            markPendingChanged();
            rerenderAllBlocks(cart);
          })
        );

        SPECIALTY_INKS.forEach((service) => {
          const isConf = service.variantId && service.variantId !== 'REPLACE_ME';
          subWrap.appendChild(
            buildRadioRow(
              `${service.label} ($${service.price.toFixed(2)}/pc)`,
              inkRadioName,
              selectedInk === service.key,
              isConf,
              () => {
                inkKeys.forEach((k) => state.subKeys.delete(k));
                state.subKeys.add(service.key);
                markPendingChanged();
                rerenderAllBlocks(cart);
              }
            )
          );
        });

        spGroup.appendChild(subWrap);
      } else {
        // Sin placement elegido, no tiene sentido dejar modificadores o
        // tinta especial "colgados" — se limpian para que no se cobren
        // servicios huérfanos que ya no se ven en pantalla.
        SCREEN_PRINT_MODIFIERS.forEach((s) => state.subKeys.delete(s.key));
        SPECIALTY_INKS.forEach((s) => state.subKeys.delete(s.key));
      }

      details.appendChild(spGroup);
    }

    // ---- Otros servicios ----
    const visibleOther = OTHER_SERVICES.filter((s) => serviceApplies(s, itemTypes));
    const otherGroups = {};
    visibleOther.forEach((s) => {
      otherGroups[s.group] = otherGroups[s.group] || [];
      otherGroups[s.group].push(s);
    });

    Object.keys(otherGroups).forEach((groupName) => {
      const groupEl = document.createElement('div');
      groupEl.className = 'wholesale-addons__group';

      const title = document.createElement('h4');
      title.className = 'wholesale-addons__group-title';
      title.textContent = groupName;
      groupEl.appendChild(title);

      otherGroups[groupName].forEach((service) => {
        const isConf = service.variantId && service.variantId !== 'REPLACE_ME';
        groupEl.appendChild(
          buildCheckboxRow(service, state.subKeys.has(service.key), isConf, (checked) => {
            if (checked) state.subKeys.add(service.key);
            else state.subKeys.delete(service.key);
            markPendingChanged();
            rerenderAllBlocks(cart);
          })
        );
      });

      details.appendChild(groupEl);
    });

    // ---- Quitar todo (por diseño) ----
    const hasClearableSelections = state.subKeys.size > 0;
    if (hasClearableSelections) {
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'wholesale-addons__clear-btn';
      clearBtn.textContent = 'Clear all services';

      clearBtn.addEventListener('click', async () => {
        const confirmed = window.confirm(
          'Are you sure you want to remove all services for this product? This cannot be undone.'
        );
        if (!confirmed) return;

        // Si el auto-sync de fondo ya está mutando el carrito en este
        // instante, esperamos a que termine en vez de correr encima de
        // él — evita el pisado de índices que vaciaba los servicios.
        if (cartMutationInProgress) {
          window.alert('Still syncing the cart, please try again in a moment.');
          return;
        }
        cartMutationInProgress = true;

        clearBtn.disabled = true;
        clearBtn.classList.add('wholesale-addons__clear-btn--loading');
        clearBtn.innerHTML = '<span class="wholesale-addons__spinner"></span>Removing…';

        state.subKeys.clear();

        try {
          let freshCart = await getCart();
          const wholesaleRoot = document.getElementById('wholesale-addons-root');
          const wholesale = isCustomerWholesale(wholesaleRoot);

          for (const line of parentLines) {
            freshCart = await applyDesiredServicesForLine(line, freshCart, { wholesale });
          }

          sessionStorage.setItem('wholesaleAddonsExpanded', 'true');
          window.location.reload();
        } catch (err) {
          console.error('[wholesale-addons]', err);
          clearBtn.disabled = false;
          clearBtn.classList.remove('wholesale-addons__clear-btn--loading');
          clearBtn.textContent = 'Clear all services (retry)';
        } finally {
          cartMutationInProgress = false;
        }
      });

      details.appendChild(clearBtn);
    }

    return details;
  }

  function renderProductBlocks(container, cart) {
    const parentLines = getParentLines(cart);
    container.innerHTML = '';

    // Agrupa todas las tallas del mismo diseño para mostrar UN solo
    // bloque por diseño en vez de uno por talla.
    const designGroups = new Map();
    parentLines.forEach((parentLine) => {
      const dk = getDesignKey(parentLine);
      if (!designGroups.has(dk)) designGroups.set(dk, []);
      designGroups.get(dk).push(parentLine);
    });

    designGroups.forEach((group) => {
      container.appendChild(buildProductBlock(group, cart));
    });
  }

  function renderMoqBanner(bannerEl) {
    bannerEl.hidden = false;
    bannerEl.textContent =
      'Set Up MOQ charge (products with screenprint): calculated per design, based on how many pieces of that specific design are in your order.';
  }

  function markPendingChanged() {
    hasPendingChanges = true;
    const btn = document.getElementById('wholesale-addons-confirm-btn');
    if (btn) {
      btn.disabled = false;
      btn.classList.remove('wholesale-addons__confirm-btn--loading');
      btn.textContent = 'Confirm services';
    }
  }

  // ------------------------------------------------------------
  // Confirmar: procesa TODOS los cambios pendientes de golpe y
  // hace un solo reload al final.
  // ------------------------------------------------------------
  function labelForKey(key) {
    const service = findServiceByKey(key);
    return service ? service.label : key;
  }

  // Aplica el estado deseado de UN producto al carrito (properties +
  // líneas de servicio agregadas/quitadas). La usan tanto "Confirm
  // services" como el botón de "Quitar todo" — misma lógica, un solo
  // lugar donde vive.
  async function applyDesiredServicesForLine(parentLine, cart, { wholesale }) {
    const designKey = getDesignKey(parentLine);
    const state = pendingState[designKey];
    if (!state) return cart;

    const itemTypes = detectItemTypes(parentLine.product_title || parentLine.title);
    const isFleece = itemTypes.has('fleece');

    // Cart fresco ANTES de calcular el MOQ — necesitamos el total de
    // piezas de ESTE diseño ahorita mismo, no el que traía el `cart`
    // que nos pasaron (puede estar viejo si cambiaron cantidades).
    cart = await getCart();
    const piecesForDesign = designPieceCount(cart, designKey);
    const moqTier = getSetupMoqTier(piecesForDesign);

    const desiredKeys = new Set(state.subKeys);
    const anyPlacementSelected = [...desiredKeys].some((k) => SCREEN_PRINT_PLACEMENT_KEYS.has(k));
    if (anyPlacementSelected && isFleece) {
      desiredKeys.add(SCREEN_PRINT_FLEECE_KEY);
    } else {
      desiredKeys.delete(SCREEN_PRINT_FLEECE_KEY);
    }

    // MOQ: cargo automático (no lo elige el cliente), calculado por
    // DISEÑO (todas sus tallas juntas), no por el carrito completo.
    // Solo aplica si el diseño tiene algún screenprint marcado. Si
    // quitan el screenprint, el MOQ se quita solo (se limpia siempre
    // primero y se vuelve a agregar solo si aplica hoy).
    MOQ_KEYS.forEach((k) => desiredKeys.delete(k));
    if (anyPlacementSelected && moqTier && moqTier.variantId && moqTier.variantId !== 'REPLACE_ME') {
      desiredKeys.add(moqTier.key);
    }

    let parent = cart.items.find((i) => i.key === parentLine.key);
    if (!parent) return cart;

    const currentAddonLines = getAddonLines(cart).filter(
      (a) => parent.properties && a.properties._for_line === parent.properties._wholesale_ref
    );
    const currentKeys = new Set(currentAddonLines.map((a) => a.properties._wholesale_addon));

    const keysEqual =
      desiredKeys.size === currentKeys.size && [...desiredKeys].every((k) => currentKeys.has(k));
    // No basta con que los KEYS coincidan — si el cliente cambió la
    // cantidad del producto con el +/- nativo del carrito (sin pasar
    // por "Confirm services"), los servicios existentes se quedan con
    // la cantidad vieja. Hay que revisar eso también antes de decidir
    // que no hay nada que hacer.
    const quantityMismatch = currentAddonLines.some((a) => a.quantity !== parent.quantity);
    if (keysEqual && !quantityMismatch) return cart; // sin cambios reales, no tocarlo

    // Una sola actualización de properties del producto (ref +
    // resumen final), hecha ANTES de tocar las líneas de servicio.
    // Shopify reposiciona la línea al final del carrito cada vez
    // que cambian sus properties — si lo hiciéramos dos veces (una
    // para el ref, otra para el resumen al final), el producto
    // terminaría reposicionado DESPUÉS de sus propios servicios.
    // Haciéndolo una sola vez y antes, los servicios que se agregan
    // después quedan justo debajo de él.
    const ref = (parent.properties && parent.properties._wholesale_ref) || generateRef();
    const desiredSummary = [...desiredKeys].map(labelForKey).join(', ');
    const idx = cart.items.indexOf(parent);
    const mergedProps = { ...(parent.properties || {}), _wholesale_ref: ref };
    if (desiredSummary) {
      mergedProps['Services Added'] = desiredSummary;
    } else {
      delete mergedProps['Services Added'];
    }
    await updateLineProperties(idx + 1, parent.quantity, mergedProps);

    cart = await getCart();
    parent = getParentByRef(cart, ref);
    const quantity = parent.quantity;
    const productLabel = buildProductLabel(parent);

    const toAdd = [...desiredKeys].filter((k) => !currentKeys.has(k));
    const toRemove = [...currentKeys].filter((k) => !desiredKeys.has(k));
    const toKeep = [...desiredKeys].filter((k) => currentKeys.has(k));

    // Sincroniza la cantidad de los servicios que se quedan igual
    // (mismo key) pero cuya línea padre cambió de cantidad — el caso
    // exacto de usar el +/- nativo del carrito en vez del modal.
    for (const key of toKeep) {
      const freshCart = await getCart();
      const addonIdx = findAddonLineIndexByRef(freshCart, key, ref);
      if (addonIdx === -1) continue;
      const addonLine = freshCart.items[addonIdx];
      if (addonLine.quantity !== quantity) {
        await updateLineQuantity(addonIdx + 1, quantity);
      }
    }

    for (const key of toAdd) {
      const service = findServiceByKey(key);
      if (!service) continue;
      const { variantId, label } = service;
      if (!variantId || variantId === 'REPLACE_ME') continue;
      await addServiceLine(variantId, key, label, quantity, ref, productLabel);
    }

    if (toRemove.length > 0) {
      const freshCart = await getCart();
      const idxs = toRemove
        .map((key) => findAddonLineIndexByRef(freshCart, key, ref))
        .filter((idx) => idx !== -1)
        .sort((a, b) => b - a);
      for (const idx of idxs) {
        await removeCartLine(idx + 1);
      }
    }

    return getCart();
  }

  async function confirmPendingChanges() {
    const btn = document.getElementById('wholesale-addons-confirm-btn');

    if (cartMutationInProgress) {
      window.alert('Still syncing the cart, please try again in a moment.');
      return;
    }
    cartMutationInProgress = true;

    if (btn) {
      btn.disabled = true;
      btn.classList.add('wholesale-addons__confirm-btn--loading');
      btn.innerHTML = '<span class="wholesale-addons__spinner"></span>Applying changes…';
    }

    try {
      let cart = await getCart();
      const parentLines = getParentLines(cart);
      const wholesaleRoot = document.getElementById('wholesale-addons-root');
      const wholesale = isCustomerWholesale(wholesaleRoot);

      for (const parentLine of parentLines) {
        cart = await applyDesiredServicesForLine(parentLine, cart, { wholesale });
      }

      sessionStorage.setItem('wholesaleAddonsExpanded', 'false');
      window.location.reload();
    } catch (err) {
      console.error('[wholesale-addons]', err);
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('wholesale-addons__confirm-btn--loading');
        btn.textContent = 'Confirm services (retry)';
      }
    } finally {
      cartMutationInProgress = false;
    }
  }

  function ensureConfirmButton(panel) {
    let btn = document.getElementById('wholesale-addons-confirm-btn');
    if (btn) return;

    btn = document.createElement('button');
    btn.type = 'button';
    btn.id = 'wholesale-addons-confirm-btn';
    btn.className = 'wholesale-addons__confirm-btn';
    btn.textContent = 'Confirm services';
    btn.disabled = true;
    btn.addEventListener('click', confirmPendingChanges);
    panel.appendChild(btn);
  }

  // ------------------------------------------------------------
  // Init / refresh
  // ------------------------------------------------------------
  async function cleanOrphanAddons(cart) {
    const parentRefs = new Set(
      getParentLines(cart)
        .map((i) => i.properties && i.properties._wholesale_ref)
        .filter(Boolean)
    );
    const orphans = getAddonLines(cart).filter((a) => !parentRefs.has(a.properties._for_line));
    if (orphans.length === 0) return false;

    const idxs = orphans.map((o) => cart.items.indexOf(o)).sort((a, b) => b - a);
    for (const idx of idxs) {
      await removeCartLine(idx + 1);
    }
    return true;
  }

  async function refreshWholesaleAddons() {
    const root = document.getElementById('wholesale-addons-root');
    if (!root) return;
    const wholesale = isCustomerWholesale(root);
    if (!wholesale) {
      root.hidden = true;
      return;
    }

    let cart = await getCart();

    // Limpieza de servicios huérfanos: si su producto fue removido del
    // carrito (con el Remove nativo), el servicio se queda "invisible"
    // pero sigue cobrando — hay que quitarlo también.
    const hadOrphans = await cleanOrphanAddons(cart);
    if (hadOrphans) {
      window.location.reload();
      return;
    }

    const pieceCount = totalPieceCount(cart);

    if (pieceCount < MIN_PIECES_FOR_ADDONS) {
      root.hidden = true;
      return;
    }

    root.hidden = false;

    const groupsContainer = document.querySelector('.wholesale-addons__groups');
    const bannerEl = document.querySelector('.wholesale-addons__moq-banner');
    const panel = document.querySelector('.wholesale-addons__panel');

    renderMoqBanner(bannerEl);
    renderProductBlocks(groupsContainer, cart);
    ensureConfirmButton(panel);
  }

  function bindToggleButton() {
    const root = document.getElementById('wholesale-addons-root');
    if (!root) return;
    const toggleBtn = root.querySelector('.wholesale-addons__toggle');
    const panel = document.querySelector('.wholesale-addons__panel');

    function openPanel() {
      toggleBtn.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      backdrop.hidden = false;
      document.body.classList.add('wholesale-addons-modal-open');
      sessionStorage.setItem('wholesaleAddonsExpanded', 'true');
    }

    function closePanel() {
      toggleBtn.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      backdrop.hidden = true;
      document.body.classList.remove('wholesale-addons-modal-open');
      sessionStorage.setItem('wholesaleAddonsExpanded', 'false');
    }

    // Backdrop (fondo oscuro clickeable para cerrar)
    let backdrop = document.getElementById('wholesale-addons-backdrop');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.id = 'wholesale-addons-backdrop';
      backdrop.className = 'wholesale-addons__backdrop';
      backdrop.hidden = true;
      document.body.appendChild(backdrop);
      backdrop.addEventListener('click', closePanel);
    }

    // Movemos el panel directo a <body>: si se queda anidado dentro de
    // los contenedores del tema (carrito, safe-sticky, etc.), su
    // z-index compite solo DENTRO de ese contenedor, y el backdrop
    // (que sí está en <body>) puede terminar tapándolo visualmente,
    // interceptando los clics antes de que lleguen al panel.
    if (panel.parentElement !== document.body) {
      document.body.appendChild(panel);
    }

    // Botón de cerrar dentro del panel (si no existe todavía)
    if (!panel.querySelector('.wholesale-addons__close-btn')) {
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'wholesale-addons__close-btn';
      closeBtn.setAttribute('aria-label', 'Close');
      closeBtn.textContent = '×';
      closeBtn.addEventListener('click', closePanel);
      panel.insertBefore(closeBtn, panel.firstChild);
    }

    if (sessionStorage.getItem('wholesaleAddonsExpanded') === 'true') {
      openPanel();
    } else {
      closePanel();
    }

    toggleBtn.addEventListener('click', () => {
      const expanded = toggleBtn.getAttribute('aria-expanded') === 'true';
      if (expanded) {
        closePanel();
      } else {
        openPanel();
      }
    });
  }

  // ------------------------------------------------------------
  // Resumen de precio unitario (producto + servicios) en el cuadro
  // de totales del carrito.
  //
  // Por qué esto NO se calcula en Liquid: WSH Wholesale Pricing mete
  // su descuento con JS DESPUÉS de que la página ya cargó — busca los
  // <td data-wpd-cart-line-price> de la tabla de order-summary y les
  // reescribe el contenido así: <s class="wpd-ext-compare-at-price">
  // $precio-original</s>$precio-con-descuento (el descontado queda
  // como texto plano después del </s>). En el momento en que Liquid
  // se ejecuta, ese descuento todavía no existe en ningún lado del
  // HTML — por eso hay que leerlo del DOM ya renderizado, no calcularlo
  // de antemano.
  // ------------------------------------------------------------
  function parseMoneyText(text) {
    // Toma el ÚLTIMO monto tipo "$1,234.56" que aparezca en el texto.
    // Si WSH ya metió su <s>$original</s>$descuento, el texto plano
    // (el descontado) queda al final — por eso "el último".
    const matches = (text || '').match(/[\d,]+\.\d{2}/g);
    if (!matches || matches.length === 0) return null;
    return parseFloat(matches[matches.length - 1].replace(/,/g, ''));
  }

  function getLineTotalFromDom(lineKey) {
    const cell = document.querySelector(`[data-wpd-cart-line-price][data-wpd-cart-item="${lineKey}"]`);
    if (!cell) return null;
    return parseMoneyText(cell.textContent);
  }

  function formatMoney(amount) {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  function setTextIfChanged(el, text) {
    // Escribir solo si el valor realmente cambió — si no, el propio
    // MutationObserver que vigila este mismo cuadro se dispararía con
    // nuestra propia escritura, entrando en un bucle infinito.
    if (el && el.textContent !== text) el.textContent = text;
  }

  function recomputeUnitTotals(cart) {
    let grandTotal = 0;
    let allRowsResolved = true;

    document.querySelectorAll('[data-unit-total-row]').forEach((row) => {
      const lineKeys = (row.dataset.lineKeys || '').split(',').filter(Boolean);
      const quantity = parseFloat(row.dataset.quantity);
      const addonKeys = (row.dataset.addonKeys || '').split(',').filter(Boolean);

      let total = 0;
      let allFound = true;

      // Producto: SÍ hay que leerlo del DOM — es el único lugar donde
      // existe el precio ya con el descuento de WSH aplicado.
      lineKeys.forEach((key) => {
        const t = getLineTotalFromDom(key);
        if (t === null) {
          allFound = false;
          return;
        }
        total += t;
      });

      // Servicios: NUNCA del DOM. WSH no los toca (están excluidos de su
      // descuento a propósito), así que su precio real vive en /cart.js,
      // que siempre está al día — leerlos del DOM corre el riesgo de
      // agarrar una celda que quedó vieja si la cantidad cambió por AJAX
      // sin que esa fila en particular se haya vuelto a pintar.
      addonKeys.forEach((key) => {
        const item = cart.items.find((i) => i.key === key);
        if (!item) {
          allFound = false;
          return;
        }
        total += item.final_line_price / 100;
      });

      if (!allFound || !quantity) {
        allRowsResolved = false;
        return; // falta algo por confirmar, se reintenta después
      }

      const unitPrice = total / quantity;
      setTextIfChanged(row.querySelector('[data-unit-price-value]'), `${formatMoney(unitPrice)}/pc`);
      setTextIfChanged(row.querySelector('[data-line-subtotal-value]'), formatMoney(total));

      grandTotal += total;
    });

    // WSH a veces pinta un Subtotal/Total (.wcp-cart-total) que NO
    // coincide con la suma real de sus propias líneas (bug confirmado
    // de su lado, reportado). Como ya tenemos el número correcto
    // (verificado contra lo que checkout cobra de verdad), lo usamos
    // para sobreescribir lo que WSH puso ahí — es un parche mientras
    // arreglan el bug, no toca nada de lo que realmente se cobra.
    if (allRowsResolved) {
      const correctText = formatMoney(grandTotal);
      // cart.items_subtotal_price es el precio REAL de Shopify (el que
      // WSH todavía no reescribió, porque su descuento es solo visual
      // vía JS sobre los <td>, no un precio real de Shopify). Si
      // nuestro grandTotal (ya con el descuento de WSH aplicado a cada
      // línea + servicios) coincide con ese número, es que este
      // cliente NO tiene descuento wholesale activo — no hay nada que
      // comparar, así que no se debe duplicar el precio.
      const rawSubtotal = cart.items_subtotal_price / 100;
      const hasRealDiscount = Math.abs(grandTotal - rawSubtotal) > 0.005;

      document.querySelectorAll('.wcp-cart-total').forEach((el) => {
        const original = el.parentElement && el.parentElement.querySelector('.wcp-original-cart-total');
        if (hasRealDiscount) {
          // Sí hay descuento real: se ve el precio original tachado
          // junto al nuestro (ya corregido), como estaba pensado.
          setTextIfChanged(el, correctText);
          if (original) {
            original.style.display = '';
            original.style.textDecoration = 'line-through';
            original.style.opacity = '0.6';
          }
        } else {
          // Sin descuento real: dejamos que se vea solo el precio
          // original de Shopify (que ya es correcto en este caso) y
          // vaciamos el nuestro para no duplicar el número.
          setTextIfChanged(el, '');
          if (original) {
            original.style.display = '';
            original.style.textDecoration = '';
            original.style.opacity = '';
          }
        }
      });
    }
  }

  // Detecta si alguna línea de producto cambió de cantidad (por
  // ejemplo con el +/- nativo del carrito, sin pasar por "Confirm
  // services") y corrige sola: sincroniza la cantidad de sus
  // servicios y recalcula el MOQ por si cambió de tier. No depende de
  // pendingState — funciona aunque el modal nunca se haya abierto en
  // esta carga de página, leyendo directo lo que el carrito ya tiene
  // marcado como referencia de "qué servicios quiere este producto".
  async function autoSyncQuantities(cart) {
    const wholesaleRoot = document.getElementById('wholesale-addons-root');
    const wholesale = isCustomerWholesale(wholesaleRoot);

    let changed = false;

    for (const parentLine of getParentLines(cart)) {
      const ref = parentLine.properties && parentLine.properties._wholesale_ref;
      if (!ref) continue;

      const addonsForLine = getAddonLines(cart).filter((a) => a.properties._for_line === ref);
      if (addonsForLine.length === 0) continue;

      const currentKeys = new Set(addonsForLine.map((a) => a.properties._wholesale_addon));
      const quantityMismatch = addonsForLine.some((a) => a.quantity !== parentLine.quantity);

      // El MOQ es por diseño — aunque esta línea en particular no haya
      // cambiado de cantidad, el tier correcto puede haber cambiado si
      // otra talla del MISMO diseño sí cambió (o se agregó/quitó).
      const designKey = getDesignKey(parentLine);
      const piecesForDesign = designPieceCount(cart, designKey);
      const correctMoqTier = getSetupMoqTier(piecesForDesign);
      const anyPlacementSelected = [...currentKeys].some((k) => SCREEN_PRINT_PLACEMENT_KEYS.has(k));
      const expectedMoqKey =
        anyPlacementSelected && correctMoqTier && correctMoqTier.variantId && correctMoqTier.variantId !== 'REPLACE_ME'
          ? correctMoqTier.key
          : null;
      const currentMoqKey = [...currentKeys].find((k) => MOQ_KEYS.has(k)) || null;
      const moqMismatch = expectedMoqKey !== currentMoqKey;

      if (!quantityMismatch && !moqMismatch) continue; // esta línea está sincronizada, nada que hacer

      // Aquí ya sabemos con certeza que hay un desfase real (como el
      // caso de 154 piezas en el producto contra 200 en sus líneas de
      // servicio) — recién aquí vale la pena avisarle al cliente que
      // se está ajustando, sin importar si esto vino del interceptor
      // de fetch, del poll de respaldo, o de la carga misma de la
      // página.
      showSyncingBanner();

      if (!pendingState[designKey]) {
        pendingState[designKey] = { subKeys: new Set(currentKeys) };
      }

      cart = await applyDesiredServicesForLine(parentLine, cart, { wholesale });
      changed = true;
    }

    return changed;
  }

  // Evita mandar un fetch de /cart.js por cada mutación que detecte el
  // observer (pueden ser varias seguidas) — junta ráfagas en una sola.
  let recomputeDebounceTimer = null;
  function scheduleRecompute() {
    if (recomputeDebounceTimer) clearTimeout(recomputeDebounceTimer);
    recomputeDebounceTimer = setTimeout(async () => {
      // Si Confirm/Clear all ya está mutando el carrito, no corremos
      // encima — el propio botón vuelve a dejar el carrito consistente
      // y este poll se reintentará solo en el próximo ciclo.
      if (cartMutationInProgress) return;

      const cart = await getCart();

      if (cartMutationInProgress) return; // pudo haber empezado mientras hacíamos el fetch

      cartMutationInProgress = true;
      let wasFixed;
      try {
        wasFixed = await autoSyncQuantities(cart);
      } finally {
        cartMutationInProgress = false;
      }

      if (wasFixed) {
        // Se deja el aviso visible: la propia recarga automática lo va
        // a reemplazar en un momento, así el cliente ve el mensaje
        // durante la transición en vez de que la pantalla se sienta
        // congelada justo antes de refrescar.
        window.location.reload();
        return;
      }

      hideSyncingBanner();
      recomputeUnitTotals(cart);
    }, 150);
  }

  function initUnitPriceSummary() {
    const rows = document.querySelectorAll('[data-unit-total-row]');
    if (rows.length === 0) return;

    scheduleRecompute();

    // Observa la tabla de order-summary (donde WSH reescribe los <td>
    // del producto) y recalcula cada vez que cambie algo ahí.
    const table = document.querySelector('.order-summary, .cart-drawer__line-items');
    if (table) {
      const observer = new MutationObserver(() => scheduleRecompute());
      observer.observe(table, { childList: true, subtree: true, characterData: true });
    }

    // También observa el cuadro de totales — si WSH vuelve a
    // sobreescribir el Subtotal/Total con su número incorrecto en
    // algún momento posterior, lo corregimos de nuevo enseguida.
    const totalsBox = document.querySelector('.cart-form__totals');
    if (totalsBox) {
      const totalsObserver = new MutationObserver(() => scheduleRecompute());
      totalsObserver.observe(totalsBox, { childList: true, subtree: true, characterData: true });
    }

    // Respaldo rápido por si WSH tarda en correr al cargar la página:
    // reintenta cada medio segundo durante los primeros ~10 segundos.
    let attempts = 0;
    const fastInterval = setInterval(() => {
      scheduleRecompute();
      attempts += 1;
      if (attempts > 20) {
        clearInterval(fastInterval);
        // Después de la carga inicial, sigue vigilando indefinido pero
        // más espaciado — es la red de seguridad real para detectar
        // cambios de cantidad hechos con el +/- nativo del carrito en
        // cualquier momento, por si el tema reemplaza toda la sección
        // del carrito de golpe y el MutationObserver de arriba se
        // queda viendo un nodo que ya no está en la página.
        setInterval(scheduleRecompute, 5000);
      }
    }, 500);
  }

  document.addEventListener('DOMContentLoaded', () => {
    bindToggleButton();
    refreshWholesaleAddons();
    initUnitPriceSummary();
  });
})();